import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { AddCircle, ArrowBack, Delete, Edit, Refresh } from '@mui/icons-material';
import { Alert, Box, Button, Chip, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';

import { useApiClient, useToastService } from '@@common';
import { ConfirmDialog, GalleryHeader, GalleryStatistics } from '@@components';

import { AuthoringResourceClient, AuthoringResourceScopeType, AuthoringResourceSummaryModel } from '../../api/api';
import { Permissions } from '../../common/Permissions';
import McpIcon from '../../common/components/McpIcon';
import { useCurrentUserPermissions } from '../../components/CurrentUserPermissionsContext';
import { resourceErrorMessage } from './resourceErrors';

type RouteParams = {
	scopeType?: 'tenant' | 'sub-domain' | 'context' | 'app';
	appId?: string;
	scopeId?: string;
	subDomainId?: string;
	contextId?: string;
};

const toScopeType = (scopeType: RouteParams['scopeType'] | undefined) => {
	switch (scopeType) {
		case 'tenant':
			return AuthoringResourceScopeType.Tenant;
		case 'sub-domain':
			return AuthoringResourceScopeType.SubDomain;
		case 'context':
			return AuthoringResourceScopeType.Context;
		case 'app':
			return AuthoringResourceScopeType.App;
		default:
			return AuthoringResourceScopeType.Tenant;
	}
};

const resolveRouteScope = ({ scopeType, scopeId, subDomainId, contextId, appId }: RouteParams) => {
	if (appId) {
		return { scopeType: AuthoringResourceScopeType.App, scopeId: appId };
	}
	if (contextId) {
		return { scopeType: AuthoringResourceScopeType.Context, scopeId: contextId };
	}

	if (subDomainId) {
		return { scopeType: AuthoringResourceScopeType.SubDomain, scopeId: subDomainId };
	}

	return {
		scopeType: toScopeType(scopeType),
		scopeId: scopeType === 'tenant' ? undefined : scopeId,
	};
};

function scopeTitle(scopeType: AuthoringResourceScopeType) {
	switch (scopeType) {
		case AuthoringResourceScopeType.Tenant:
			return 'Services Resources';
		case AuthoringResourceScopeType.SubDomain:
			return 'SubDomain Resources';
		case AuthoringResourceScopeType.App:
			return 'App Resources';
		default:
			return 'Context Resources';
	}
}

interface ResourceRowProps {
	resource: AuthoringResourceSummaryModel;
	onDelete: (resource: AuthoringResourceSummaryModel) => void;
	onEdit: (resource: AuthoringResourceSummaryModel) => void;
	canDelete: boolean;
	inherited: boolean;
}

function ResourceRow({ resource, onDelete, onEdit, canDelete, inherited }: ResourceRowProps) {
	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: 2,
				border: (theme) => `1px solid ${theme.palette.divider}`,
				borderRadius: 1,
				px: 2,
				py: 1.25,
			}}
		>
			<Box sx={{ minWidth: 0, flexGrow: 1 }}>
				<Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
					<Typography variant="subtitle2" noWrap>
						{resource.name}
					</Typography>
					<Chip label={resource.isEnabled ? 'Enabled' : 'Disabled'} size="small" variant="outlined" color={resource.isEnabled ? 'success' : 'default'} />
					{resource.isMcpExposed && (
						<Chip
							icon={<McpIcon size={14} />}
							label="MCP"
							size="small"
							variant="outlined"
							color="primary"
							sx={{
								'& .MuiChip-icon': {
									color: 'primary.main',
									mr: -0.5,
								},
								'& .MuiChip-label': {
									pl: 0.25,
								},
							}}
						/>
					)}
					<Chip label={resource.mimeType} size="small" variant="outlined" />
					<Chip label={inherited ? 'Inherited' : 'Direct'} size="small" variant="outlined" />
					{resource.targets.map((target) => (
						<Chip
							key={`${target.type}:${target.id ?? ''}`}
							size="small"
							label={target.type === AuthoringResourceScopeType.Tenant ? 'Entire tenant' : target.type}
						/>
					))}
				</Stack>
				<Typography variant="body2" color="text.secondary" noWrap>
					{resource.description || resource.uri}
				</Typography>
				{resource.tags.length > 0 && (
					<Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: 'wrap' }}>
						{resource.tags.map((tag) => (
							<Chip key={tag} label={tag} size="small" />
						))}
					</Stack>
				)}
			</Box>
			<Tooltip title="Edit resource">
				<IconButton size="small" onClick={() => onEdit(resource)}>
					<Edit fontSize="small" />
				</IconButton>
			</Tooltip>
			<Tooltip title="Delete resource">
				<IconButton size="small" color="error" disabled={!canDelete} onClick={() => onDelete(resource)}>
					<Delete fontSize="small" />
				</IconButton>
			</Tooltip>
		</Box>
	);
}

export default function AuthoringResourcesView() {
	const routeParams = useParams<RouteParams>();
	const navigate = useNavigate();
	const location = useLocation();
	const client = useApiClient(AuthoringResourceClient);
	const toastService = useToastService();
	const queryClient = useQueryClient();
	const { hasPermission } = useCurrentUserPermissions();
	const { scopeType, scopeId } = resolveRouteScope(routeParams);
	const globalLibrary = location.pathname === '/resources';
	const [deleteTarget, setDeleteTarget] = useState<AuthoringResourceSummaryModel | null>(null);

	const queryKey = ['authoringResources', globalLibrary ? 'all' : scopeType, scopeId];
	const { data, isLoading, refetch, error } = useQuery({
		queryKey,
		queryFn: () =>
			client.list(globalLibrary ? undefined : scopeType, scopeType === AuthoringResourceScopeType.Tenant ? undefined : scopeId, true, !globalLibrary),
	});

	const deleteMutation = useMutation({
		mutationFn: (resource: AuthoringResourceSummaryModel) => client.delete(resource.id),
		onSuccess: async () => {
			toastService.success('Resource deleted.');
			setDeleteTarget(null);
			await queryClient.invalidateQueries({ queryKey });
		},
		onError: (error) => toastService.error(resourceErrorMessage(error, 'Failed to delete resource.')),
	});

	const getReturnTo = () => `${location.pathname}${location.search}`;

	const handleCreate = () => {
		const query = new URLSearchParams();
		const createScope = globalLibrary && !hasPermission(Permissions.ServiceUpdate) ? AuthoringResourceScopeType.App : scopeType;
		query.set('scopeType', createScope);
		if (scopeType !== AuthoringResourceScopeType.Tenant && scopeId) {
			query.set('scopeId', scopeId);
		}
		query.set('returnTo', getReturnTo());
		navigate(`/resources/new?${query.toString()}`);
	};

	const handleEdit = (resource: AuthoringResourceSummaryModel) => {
		const query = new URLSearchParams();
		query.set('returnTo', getReturnTo());
		navigate(`/resources/${resource.id}?${query.toString()}`);
	};

	const resources = data ?? [];
	return (
		<Stack direction="column" spacing={2} sx={{ flexGrow: 1, minHeight: 0, p: (theme) => `${theme.layout.edgeGap}px`, overflow: 'hidden' }}>
			<GalleryHeader
				title={globalLibrary ? 'Resources' : scopeTitle(scopeType)}
				subtitle="Store and manage reusable resources across scopes."
				utilityActions={
					<Stack direction="row" spacing={1}>
						<Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
							Back
						</Button>
						<Tooltip title="Refresh resources">
							<IconButton aria-label="Refresh resources" onClick={() => refetch()}>
								<Refresh />
							</IconButton>
						</Tooltip>
					</Stack>
				}
				primaryAction={
					<Button
						variant="contained"
						startIcon={<AddCircle />}
						onClick={handleCreate}
						disabled={!hasPermission(Permissions.ServiceUpdate) && !hasPermission(Permissions.AppUpdate)}
					>
						Resource
					</Button>
				}
				statistics={<GalleryStatistics items={[{ label: 'Resources on this page', value: resources.length }]} />}
			/>
			<Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex' }}>
				<Paper variant="outlined" sx={{ p: 2, flexGrow: 1, minHeight: 0, overflow: 'auto' }}>
				<Stack spacing={1.5}>
					{error && <Alert severity="error">{resourceErrorMessage(error, 'Unable to load resources.')}</Alert>}
					{!isLoading && resources.length === 0 ? (
						<Box sx={{ border: (theme) => `1px dashed ${theme.palette.divider}`, borderRadius: 1, py: 6, textAlign: 'center' }}>
							<Typography variant="subtitle1">No resources</Typography>
						</Box>
					) : (
						resources.map((resource) => (
							<ResourceRow
								key={resource.id}
								resource={resource}
								onDelete={setDeleteTarget}
								onEdit={handleEdit}
								canDelete={resource.targets.every((target) =>
									hasPermission(target.type === AuthoringResourceScopeType.App ? Permissions.AppDelete : Permissions.ServiceDelete),
								)}
								inherited={!globalLibrary && !resource.targets.some((target) => target.type === scopeType && (target.id ?? undefined) === scopeId)}
							/>
						))
					)}
				</Stack>
				</Paper>
			</Box>
			<ConfirmDialog
				open={!!deleteTarget}
				title="Delete Resource"
				message={deleteTarget ? `Delete "${deleteTarget.name}" for all of its targets? To change where it applies, edit its targets instead.` : ''}
				confirmText="Delete"
				confirmColor="error"
				onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget)}
				onCancel={() => setDeleteTarget(null)}
			/>
		</Stack>
	);
}
